// src/components/Map/mapbox.config.js
// Centralized Mapbox configuration with best practices

export const MAPBOX_CONFIG = Object.freeze({
  //  Access token (single source of truth) - WITH FALLBACK
  ACCESS_TOKEN: process.env.REACT_APP_MAPBOX_TOKEN || '',

  //  Map rendering options
  MAP_OPTIONS: Object.freeze({
    style: 'mapbox://styles/bfretdev/cmjbgzoau006m01qydv5jares',
    center: [0, 20],
    zoom: 2,
    minZoom: 1,
    maxZoom: 18,
    bearing: 0,
    pitch: 0,
    antialias: true,
    attributionControl: false,
    failIfMajorPerformanceCaveat: true,
    preserveDrawingBuffer: false,
  }),

  //  Billing & usage optimizations
  BILLING_OPTIMIZATIONS: Object.freeze({
    autoZoomCooldown: 30000,           // 30 seconds between auto-zooms
    maxAutoZoomPerSession: 10,         // Max auto-zooms per session
    clusteringThreshold: 10,           // Enable clustering at 10+ markers
    viewportBuffer: 0.1,               // Degrees buffer for visible markers
    maxConcurrentRequests: 5,          // Max parallel API requests
    minCharsForSearch: 2,              // Minimum chars for search (snappier)
    searchDebounceMs: 200,             // Lower debounce for responsiveness
    cacheTTL: 15 * 60 * 1000,          // 15 minutes cache
    maxRequestsPerMinute: 600,         // ~100ms delay between requests (much faster)
  }),

  //  Performance tuning
  PERFORMANCE: Object.freeze({
    MAX_MARKERS_WITHOUT_CLUSTERING: 50,
    MARKER_CANVAS_THRESHOLD: 200,      // Use canvas for 200+ markers
    TILE_CACHE_SIZE: 100,
    MAX_CLUSTER_RADIUS: 80,
    MAX_POPUP_OPEN: 1,                 // Only one popup at a time
  }),

  //  API endpoints for services (e.g., geocoding)
  API_ENDPOINTS: Object.freeze({
    GEOCODING: 'https://api.mapbox.com/geocoding/v5/mapbox.places',
    SEARCH_BOX_SUGGEST: 'https://api.mapbox.com/search/searchbox/v1/suggest',
    SEARCH_BOX_RETRIEVE: 'https://api.mapbox.com/search/searchbox/v1/retrieve',
  }),

  //  Default values for search/autocomplete
  DEFAULTS: Object.freeze({
    SEARCH_LIMIT: 5,
    COUNTRY: null,
    LANGUAGE: 'fr',
  }),

  //  TomTom Search API configuration (proxied through backend)
  TOMTOM: Object.freeze({
    BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
    ENDPOINT: '/api/public/search/tomtom',
  }),

  //  Google Maps API configuration (proxied through backend)
  GOOGLE: Object.freeze({
    BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
    AUTOCOMPLETE_ENDPOINT: '/api/public/search/google/autocomplete',
    DETAILS_ENDPOINT: '/api/public/search/google/details',
  }),
});

// Helper: merge custom options into default MAP_OPTIONS
export const getMapOptions = (customOptions = {}) => ({
  ...MAPBOX_CONFIG.MAP_OPTIONS,
  ...customOptions,
});

// Safe token getter with validation
export const getMapboxAccessToken = () => {
  const token = MAPBOX_CONFIG.ACCESS_TOKEN;

  if (!token || token.trim() === '') {
    console.error(' Mapbox token is empty or not configured!');
    console.error('Please set REACT_APP_MAPBOX_TOKEN in your .env file');

    if (process.env.NODE_ENV === 'development') {
      console.debug('Current NODE_ENV:', process.env.NODE_ENV);
      console.debug('REACT_APP_MAPBOX_TOKEN:', process.env.REACT_APP_MAPBOX_TOKEN);
      console.debug('All env variables starting with REACT_APP:');
      Object.keys(process.env)
        .filter(key => key.startsWith('REACT_APP'))
        .forEach(key => console.debug(`  ${key}: ${process.env[key] ? '***SET***' : 'NOT SET'}`));
    }

    return null;
  }

  if (token.includes('YOUR_MAPBOX_TOKEN') || token.includes('pk.ey')) {
    console.warn(' Mapbox token appears to be a placeholder or test token');
  }

  return token;
};

// Validate configuration
export const validateMapboxConfig = () => {
  const token = getMapboxAccessToken();
  const hasValidToken = token !== null;

  if (!hasValidToken) {
    return {
      isValid: false,
      message: 'Mapbox access token is missing or invalid'
    };
  }

  // Check if endpoints are defined
  if (!MAPBOX_CONFIG.API_ENDPOINTS.GEOCODING) {
    console.warn('Mapbox geocoding endpoint is not configured');
  }

  return {
    isValid: true,
    message: 'Mapbox configuration is valid'
  };
};

// Create map options with token included
export const createMapOptions = (customOptions = {}) => {
  const token = getMapboxAccessToken();

  if (!token) {
    throw new Error('Cannot create map options: Mapbox access token is missing');
  }

  return {
    ...MAPBOX_CONFIG.MAP_OPTIONS,
    ...customOptions,
    accessToken: token, //  Token passed to Map constructor
  };
};

// Backward-compatible exports
export const MAP_OPTIONS = MAPBOX_CONFIG.MAP_OPTIONS;
export const BILLING_OPTIMIZATIONS = MAPBOX_CONFIG.BILLING_OPTIMIZATIONS;
export const PERFORMANCE = MAPBOX_CONFIG.PERFORMANCE;
export const API_ENDPOINTS = MAPBOX_CONFIG.API_ENDPOINTS;
export const DEFAULTS = MAPBOX_CONFIG.DEFAULTS;